import React, { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import NotificationItem from './NotificationItem';
import './notificationPanel.less';
import { Button, Drawer, Input, Switch, Tooltip } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { enableDisableNotifications, fetchNotifications, readNotifications, saveNotifications } from '../../../store/actions/utilAction';
import { centeredNotification } from '../../../utils/utility';
import { RootState } from '../../../store';
import { customGTMEvent } from '../../../modules/crm/inPatient/service/crmUtils';
interface INotificationPanel {
  isNotificationPanelVisible: boolean;
  handleMarkAllAsRead: () => void;
  onClose: () => void;
  unreadNotificationsList: any[];
  setUnreadNotificationsList: any;
}
const { Search } = Input;

const NotificationPanel = ({
  isNotificationPanelVisible,
  handleMarkAllAsRead,
  onClose,
  unreadNotificationsList,
  setUnreadNotificationsList,
}: INotificationPanel): ReactElement => {
  const dispatch = useDispatch();
  const [isNotificationEnabled, setIsNotificationEnabled] =
    useState<boolean>(localStorage.getItem('isNotificationEnabled') === 'true' ? true : false);
  const [startIndex, setStartIndex] = useState<number>(1);
  const [searchText, setSearchText] = useState<string>('');
  const [range] = useState<number>(10);
  const { notifications: notificationSelector, totalNotificationUnReadCount, totalNotificationCount } = useSelector(
    (state: RootState): any => state.util,
  ) || [];
  const stopScrollAt = Math.round(totalNotificationCount / range);

  const observer: any = useRef();
  const lastThirdElement = useCallback((node: any): any => {
    if (observer.current) {
      observer?.current?.disconnect();
    }
    observer.current = new IntersectionObserver((entries: any): any => {
      if (entries[0]?.isIntersecting) {
        setStartIndex((prevState: any): any => prevState <= stopScrollAt - 1 ? prevState + 1 : prevState);
      }
    }, {
      threshold: 0.5,
    });
    if (node) {
      observer?.current?.observe(node);
    }
  }, []);

  useEffect((): any => {
    if (startIndex > 1 && notificationSelector?.length < totalNotificationCount) {
      fetchNotificationData();
    }
  }, [startIndex]);

  const fetchNotificationData = (index: any = startIndex, text: any = searchText): any => {
    if (process.env.REACT_APP_IS_NOTIFICATIONS_ENABLED === 'true' && localStorage.getItem('isNotificationEnabled') === 'true') {
      dispatch(fetchNotifications({startIndex: index, searchText: text, range }));
    }
  };

  const enableOrDisableNotifications = (e: boolean): void => {
    customGTMEvent({
      event: e
        ? 'Notification toggle switched ON'
        : 'Notification toggle switched OFF',
    });
    dispatch(enableDisableNotifications(!isNotificationEnabled, onSuccessIsEnabled, onFailureIsEnabled));
  };

  const onSuccessIsEnabled = (): void => {
    if (unreadNotificationsList?.length) {
      dispatch(readNotifications(unreadNotificationsList, onSuccessRead, onFailureRead, false));
    }
    setStartIndex(1);
    localStorage.setItem('isNotificationEnabled', String(!isNotificationEnabled));
    setIsNotificationEnabled(!isNotificationEnabled);
    if (localStorage.getItem('isNotificationEnabled') === 'true') {
      dispatch(saveNotifications([]));
      fetchNotificationData();
    }
  };

  const onFailureIsEnabled = (errorMessage: string): void => {
    centeredNotification(errorMessage);
  };

  const onSuccessRead = (): void => {
    setUnreadNotificationsList([]);
  };

  const onFailureRead = (errorMessage: string): void => {
    centeredNotification(errorMessage);
  };

  const onChange = (e: any): void => {
    setStartIndex(1);
    setSearchText(e);
    localStorage.setItem('currentSearchText', e);
    if (e === '') {
      dispatch(saveNotifications([]));
      fetchNotificationData(1, '');
    }
  };

  const onSearch = (e: any): void => {
    if (notificationSelector?.length < totalNotificationCount && e !== '') {
      dispatch(saveNotifications([]));
      fetchNotificationData();
    }
  };

  return (
    <Drawer
      placement="right"
      closable={false}
      closeIcon={<Button danger={true}>Close</Button>}
      onClose={onClose}
      visible={isNotificationPanelVisible}
      width={714}
      getContainer={document.getElementById('root') || false}
      style={{
        position: 'fixed',
      }}
      className={'notification-drawer'}
    >
      <div className="ant-drawer-header">
        {isNotificationEnabled ? `Notifications (${totalNotificationUnReadCount})` : 'Notifications'}
        <Tooltip title={isNotificationEnabled ? 'Turn off to stop receiving notifications' : 'Turn on to start receiving notifications'}
          overlayStyle={{ maxWidth: '300px' }} placement="bottom">
          <Switch checked={isNotificationEnabled} className={isNotificationEnabled ? 'notification-toggle margin-switch-on' : 'notification-toggle'} size="small"
            onClick={(e: boolean): void => enableOrDisableNotifications(e)} />
        </Tooltip>
        <div>
          {isNotificationEnabled &&
            <Button
              onClick={handleMarkAllAsRead}
              className={Boolean(totalNotificationUnReadCount) ? 'mark-read' : 'mark-read grayed-out'}
              type="link"
              disabled={!isNotificationEnabled || !Boolean(totalNotificationUnReadCount)}
            >
              Mark all as read
            </Button>
          }
          <Button onClick={onClose} danger={true}>
            Close
          </Button>
        </div>
      </div>
      <div className="ant-drawer-body">
        {isNotificationEnabled &&
          <div>
            <div className="search-div">
              <Search
                type="search"
                placeholder="Search"
                onChange={(e: any): any => onChange(e?.currentTarget?.value)}
                value={searchText}
                className="search-input"
                allowClear={true}
                onSearch={(e: any): any => onSearch(e)}
                enterButton={true}
              />
            </div>
            {notificationSelector.map(
              (notification: any, index: number): ReactElement => {
                if (notificationSelector?.length - 3 === index + 1 && startIndex <= stopScrollAt - 1) {
                  return (
                    <a
                      ref={lastThirdElement}
                      key={index}
                    >
                      <NotificationItem
                        data={notification}
                        key={index}
                        highlight={searchText}
                        onClose={onClose}
                        setUnreadNotificationsList={setUnreadNotificationsList}
                      />
                      {index !== notificationSelector.length - 1 && (
                        <div className={'divider'} />
                      )}
                    </a>
                  );
                } else {
                  return (
                    <a
                      key={index}
                    >
                      <NotificationItem
                        data={notification}
                        key={index}
                        highlight={searchText}
                        onClose={onClose}
                        setUnreadNotificationsList={setUnreadNotificationsList}
                      />
                      {index !== notificationSelector.length - 1 && (
                        <div className={'divider'} />
                      )}
                    </a>
                  );
                }
              },
            )}
          </div>
        }
        {
          isNotificationEnabled && !notificationSelector?.length &&
          <div className="no-notifications-found">
              No notifications found
          </div>
        }
        {!isNotificationEnabled &&
          <div className="notification-disabled">
            To get Notifications, please turn on the toggle
          </div>
        }
      </div>
    </Drawer>
  );
};

export default NotificationPanel;
